import React, { useState, useEffect, useRef } from 'react'
import { Row, Col, Button, Select } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import SettingsTabs from './SettingsTabs'
import { isAdmin, isManager, useSession } from '@slerp/accounts'
import { useQuery } from '@apollo/client'
import { Route, useNavigate, Navigate, Routes } from 'react-router-dom'
import Loading from '../Utils/Loading'
import GeneralSettings from './GeneralSettings'
import TableSettings from './TableSettings'
import PreOrderSettings from './PreOrderSettings'
import SameDaySettings from './SameDaySettings'
import { useParams } from 'react-router-dom'
import { Store, Merchant } from '@slerp/controls'
import { QUERY_MERCHANT_STORES } from './LocationQueries'
import { GET_MERCHANT_ORDER_STORES } from './Dashboard/DashboardQueries'
import { LocationIcon, SelectDownArrowIcon } from '@slerp/assets'
import { SelectPrefixWrapper } from './../Widgets/'
import AccountsAndBilling from './AccountAndBilling'
import { useGlobalQuery } from 'hooks/useGlobalQuery'

export interface LocationSettingProps {
  merchant: Merchant
  store: Store
  refetch: () => void
  disabled?: boolean
}

interface SameDayOrderStoresData {
  id: string
  slug: string
  name: string
}

const Settings = () => {
  const { merchant: merchantSession, user } = useSession()
  const { slug } = useParams<{ slug: string }>()
  const { isLoading } = useGlobalQuery()
  const navigate = useNavigate()
  const currentLocation = window.location.pathname.match(/settings.*/)

  const [sameDayOrderStores, setSameDayOrderStores] = useState<
    Array<SameDayOrderStoresData>
  >([])

  const { data, refetch } = useQuery(QUERY_MERCHANT_STORES, {
    variables: { merchant_id: merchantSession?.id, slug },
    fetchPolicy: 'no-cache'
  })

  const { data: sameDayOrderStoresData } = useQuery(GET_MERCHANT_ORDER_STORES, {
    variables: {
      merchantId: merchantSession?.id,
      userEmail: user?.email
    }
  })

  useEffect(() => {
    if (sameDayOrderStoresData) {
      const { stores } = sameDayOrderStoresData
      setSameDayOrderStores(stores)
    }
  }, [sameDayOrderStoresData])

  const merchant = data?.merchant && data.merchant
  const store = data?.merchant.stores[0]

  const moveToStore = (storeSlug: string) => {
    window.location.pathname = `/location/${storeSlug}/${currentLocation}`
  }

  const canManage = isAdmin(user) || isManager(user)

  const ConnectRedirect = () => {
    const done = useRef(false)
    useEffect(() => {
      if (!done.current) {
        done.current = true
        localStorage.removeItem('redirect-store')
      }
    }, [])
    return <Navigate to={`/accounts-and-billing`} replace />
  }

  const SettingRoutes = () => (
    <Routes>
      <Route
        path={`/general/:subtab?`}
        element={
          <GeneralSettings
            merchant={merchant}
            store={store}
            refetch={refetch}
          />
        }
      />

      <Route
        path={`/same-day/:subtab`}
        element={
          <SameDaySettings
            merchant={merchant}
            store={store}
            refetch={refetch}
          />
        }
      />

      <Route
        path={`/pre-order/:subtab?`}
        element={
          <PreOrderSettings
            merchant={merchant}
            store={store}
            refetch={refetch}
          />
        }
      />

      <Route
        path={`/table/:subtab?`}
        element={
          <TableSettings
            merchant={data.merchant}
            store={store}
            refetch={refetch}
          />
        }
      />

      {/* Default redirects */}
      <Route path={`/`} element={<Navigate to={`/general/details`} />} />

      <Route path={`/general`} element={<Navigate to={`/general/details`} />} />

      <Route
        path={`/pre-order`}
        element={<Navigate to={`/pre-order/cut-off-times`} />}
      />

      <Route path={`/table`} element={<Navigate to={`/table/details`} />} />

      <Route
        path={`/same-day`}
        element={<Navigate to={`/same-day/order-types`} />}
      />

      <Route
        path={`/same-day/details`}
        element={<Navigate to={`/same-day/order-types`} />}
      />

      {merchant.setting.franchise_enabled && canManage && (
        <>
          <Route path={`/connect-redirect`} element={<ConnectRedirect />} />

          <Route
            path={`/accounts-and-billing`}
            element={
              <AccountsAndBilling
                merchant={data.merchant}
                store={store}
                refetch={refetch}
              />
            }
          />
        </>
      )}
    </Routes>
  )

  return (
    <>
      <Row justify='space-between' className='_mb-8'>
        <Col>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              margin: '0 0 20px 0'
            }}
            onClick={() =>
              navigate(`/location/${slug}/orders`, { replace: true })
            }
          >
            <Button
              icon={<ArrowLeftOutlined />}
              style={{ paddingTop: '2px', borderRadius: '4px' }}
            />
            <span
              style={{
                cursor: 'pointer',
                margin: '0 0 0 8px'
              }}
            >
              Back to <strong>ORDERS</strong> and <strong>INVENTORY</strong>
            </span>
          </div>
        </Col>
        <Col>
          {data && sameDayOrderStores && (
            <SelectPrefixWrapper prefixicon={<LocationIcon />}>
              <SelectPrefixWrapper prefixicon={<LocationIcon />}>
                <Select
                  suffixIcon={<SelectDownArrowIcon />}
                  className='_w-100'
                  defaultValue={sameDayOrderStores && slug}
                  onChange={moveToStore}
                  style={{
                    minWidth: 'max-content'
                  }}
                  dropdownStyle={{
                    minWidth: '200px'
                  }}
                >
                  {sameDayOrderStores.map((store) => (
                    <Select.Option key={store.id} value={store.slug}>
                      {store.name}
                    </Select.Option>
                  ))}
                </Select>
              </SelectPrefixWrapper>
            </SelectPrefixWrapper>
          )}
        </Col>
      </Row>
      <Row data-testid='settings-form'>
        <Col span={24}>
          <SettingsTabs merchant={merchant} />
        </Col>
        <Col span={24}>
          {(data && data.merchant && store && !isLoading && (
            <SettingRoutes />
          )) || <Loading />}
        </Col>
      </Row>
    </>
  )
}
export default Settings
